/**
 * „KB-würdig" am gelösten Ticket (Spec 9.4).
 *
 * Ablauf: Knopf → die KI baut aus dem Verlauf einen anonymisierten Entwurf →
 * der Agent redigiert ihn im Formular → er speichert. **Der Entwurf wird nie
 * automatisch veroeffentlicht** — dasselbe Muster wie beim Antwort-Entwurf.
 */
import type { KbArticleDraft } from '@tickethub/shared';
import { BookPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type KbCuratePanelProps = {
  disabled: boolean;
  onDraft: () => Promise<KbArticleDraft>;
  onSave: (input: { title: string; body_text: string; source_ticket_id: string }) => Promise<void>;
};

export function KbCuratePanel({ disabled, onDraft, onSave }: KbCuratePanelProps) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<KbArticleDraft | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  async function generate() {
    setBusy(true);
    try {
      const result = await onDraft();
      setDraft(result);
      setTitle(result.title);
      setBody(result.body_text);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    try {
      await onSave({
        title: title.trim(),
        body_text: body.trim(),
        source_ticket_id: draft.source_ticket_id,
      });
      setDraft(null);
      setTitle('');
      setBody('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label={t('ai.curate_title')}>
      <div className="flex items-center gap-2">
        <BookPlus aria-hidden="true" className="size-4" />
        <h2 className="text-sm font-medium">{t('ai.curate_title')}</h2>
      </div>

      {draft === null ? (
        <>
          <p className="text-sm text-muted-foreground">{t('ai.curate_hint')}</p>
          <Button size="sm" variant="outline" disabled={disabled || busy} onClick={() => void generate()}>
            {busy ? t('common.loading') : t('ai.curate_start')}
          </Button>
        </>
      ) : (
        <>
          <div className="space-y-2">
            <Label htmlFor="curate-title">{t('ai.kb_article_title')}</Label>
            <Input id="curate-title" value={title} onChange={(event) => setTitle(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="curate-body">{t('ai.kb_article_body')}</Label>
            <textarea
              id="curate-body"
              className="min-h-40 w-full rounded-md border border-input bg-background p-2 text-sm"
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy || title.trim().length === 0 || body.trim().length === 0}
              onClick={() => void save()}
            >
              {t('ai.curate_save')}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
